import type { PaymentStatus, PaymentTabId } from "./types/revenue.types";
import type { UserPlan } from "../users";

/**
 * The palette a payment's status is drawn in.
 *
 * The same four tints the rest of the panel uses, assigned by what the reader
 * is meant to do about the row rather than by how the states sort:
 *
 * - teal is the live one, as it is on the roster and in the billing history;
 * - green is money that arrived and a period that closed cleanly — distinct
 *   from teal because "still running" and "finished, paid" are the two states
 *   somebody scanning this column most needs to tell apart;
 * - red is a failure to collect, which is the only row here worth chasing;
 * - amber is a checkout somebody walked away from — a lost sale rather than a
 *   fault, so it is marked without being alarming;
 * - grey is a payment still in flight, where there is nothing to do but wait.
 */
export const PAYMENT_STATUS_STYLES: Record<PaymentStatus, string> = {
  Active: "bg-brand-soft text-[#00899B]",
  Completed: "bg-[#DDF3E4] text-[#2F8E5B]",
  Expired: "bg-[#FDECEC] text-[#D14343]",
  Abandoned: "bg-[#FDF0DC] text-[#B4761A]",
  "Pending authorization": "bg-[#F1F4F6] text-ink-muted",
};

/** What each tab is called, in the order the tab bar offers them. */
export const PAYMENT_TABS: { id: PaymentTabId; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "abandoned", label: "Abandoned" },
  { id: "expired", label: "Expired" },
  { id: "pending_authorization", label: "Pending authorization" },
];

/**
 * What the Duration column prints, worked out from the plan.
 *
 * Derived rather than read, because there is nothing to read: the endpoint
 * sends a plan and an amount and no period. It is a safe derivation — the
 * plan *is* the period, which is what "Monthly" and "Yearly" mean — and doing
 * it here keeps the one place that would have to change if a quarterly plan
 * ever appears.
 *
 * A free trial has no duration in this column because it has no payment: a
 * trial is never billed, so a row on this screen should never carry one.
 */
export const PLAN_DURATION: Record<UserPlan, string> = {
  Monthly: "1 month",
  Yearly: "1 year",
  "Free trial": "—",
};
